/**
 * Felix AI Avatar — animated visual indicator of Felix's current state.
 *
 * States:
 * - listening: calm pulse, receptive indicator
 * - thinking: processing animation, rotating elements
 * - speaking: active waveform, expressive animation
 * - paused: dim/idle state
 * - muted: crossed-out indicator
 *
 * The avatar is an SVG-based component with CSS animations that reflect
 * Felix's real-time voice state during meetings. It connects to the
 * felix_state messages from Redis/data channels.
 */

import { useId, useMemo } from "react";
import { cn } from "@/lib/utils";

export type FelixVoiceState = "listening" | "thinking" | "speaking" | "paused" | "muted";

/**
 * Avatar CORE-SHAPE variant. Orthogonal to voice-state: the variant changes
 * ONLY the geometry of the inner "Felix core" glyph (the filled `<circle>` in
 * the default look), while STATE_COLORS / glow / outer ring / animations /
 * sizes are all preserved and still driven by `state`. This lets an org pick a
 * personal Felix silhouette (OB-01 onboarding avatar-pick) without touching the
 * live voice-state color language.
 */
export type FelixAvatarVariant = "orb" | "hex" | "diamond" | "rings" | "pulse";

/**
 * Single source of truth for the pickable variants — imported by the
 * onboarding picker AND the tests so the option list can never drift from what
 * the component actually renders. `orb` is first == the current default look.
 */
export const FELIX_AVATAR_VARIANTS: { id: FelixAvatarVariant; label: string }[] = [
  { id: "orb", label: "Orb" },
  { id: "hex", label: "Hexagon" },
  { id: "diamond", label: "Diamond" },
  { id: "rings", label: "Rings" },
  { id: "pulse", label: "Pulse" },
];

interface FelixAvatarProps {
  state: FelixVoiceState;
  size?: "sm" | "md" | "lg";
  /** Core-shape geometry. Defaults to "orb" (the original filled circle), so
   *  every existing call site is visually unchanged. */
  variant?: FelixAvatarVariant;
  rolePreset?: string;
  aggressiveness?: string;
  showLabel?: boolean;
  className?: string;
}

/**
 * Compute the SVG element(s) for the chosen core shape. Every variant is
 * inscribed inside the same `dims.inner` diameter the original circle used, so
 * it lines up with the outer ring / glyph / waveform at all 3 sizes and keeps
 * the exact same state-driven `fill`, `opacity`, and breathe animation.
 */
function renderCore({
  variant,
  center,
  inner,
  fill,
  opacity,
  coreClass,
}: {
  variant: FelixAvatarVariant;
  center: number;
  inner: number;
  fill: string;
  opacity: number;
  coreClass: string;
}) {
  const r = inner / 2;

  if (variant === "hex") {
    // Regular hexagon inscribed in radius r (flat-top), rotated -90° so a
    // vertex points up — a crisp, stable silhouette.
    const points = Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      return `${center + r * Math.cos(a)},${center + r * Math.sin(a)}`;
    }).join(" ");
    return (
      <polygon points={points} fill={fill} opacity={opacity} className={coreClass} />
    );
  }

  if (variant === "diamond") {
    // Rhombus — a square rotated 45° (vertices at N/E/S/W of the core circle).
    const points = [
      `${center},${center - r}`,
      `${center + r},${center}`,
      `${center},${center + r}`,
      `${center - r},${center}`,
    ].join(" ");
    return (
      <polygon points={points} fill={fill} opacity={opacity} className={coreClass} />
    );
  }

  if (variant === "rings") {
    // 3 concentric thin ring strokes (no fill) — same color, stepped radii so
    // the outermost still fits inside `inner`.
    const radii = [r, r * 0.68, r * 0.36];
    return (
      <>
        {radii.map((rr, i) => (
          <circle
            key={i}
            cx={center}
            cy={center}
            r={rr}
            fill="none"
            stroke={fill}
            strokeWidth={Math.max(1, inner / 16)}
            opacity={opacity}
            className={i === 0 ? coreClass : ""}
          />
        ))}
      </>
    );
  }

  if (variant === "pulse") {
    // A small cluster of vertical waveform bars, centered on the core and
    // inscribed within `inner`. Static heights (voice-state animation is still
    // carried by the separate speaking-waveform + breathe classes).
    const heights = [0.45, 0.75, 1, 0.75, 0.45];
    const barW = Math.max(1.5, inner / 10);
    const gap = barW * 0.7;
    const totalW = heights.length * barW + (heights.length - 1) * gap;
    const startX = center - totalW / 2;
    return (
      <>
        {heights.map((h, i) => {
          const barH = inner * h;
          return (
            <rect
              key={i}
              x={startX + i * (barW + gap)}
              y={center - barH / 2}
              width={barW}
              height={barH}
              rx={barW / 2}
              fill={fill}
              opacity={opacity}
              className={i === 2 ? coreClass : ""}
            />
          );
        })}
      </>
    );
  }

  // "orb" (default) — the original filled circle core.
  return (
    <circle cx={center} cy={center} r={r} fill={fill} opacity={opacity} className={coreClass} />
  );
}

const SIZE_MAP = {
  sm: { outer: 40, inner: 20, ring: 32, fontSize: 10 },
  md: { outer: 64, inner: 32, ring: 52, fontSize: 12 },
  lg: { outer: 96, inner: 48, ring: 80, fontSize: 14 },
};

const STATE_COLORS = {
  listening: { primary: "#3B82F6", secondary: "#93C5FD", glow: "rgba(59, 130, 246, 0.3)" },
  thinking: { primary: "#F59E0B", secondary: "#FCD34D", glow: "rgba(245, 158, 11, 0.3)" },
  speaking: { primary: "#10B981", secondary: "#6EE7B7", glow: "rgba(16, 185, 129, 0.4)" },
  paused: { primary: "#6B7280", secondary: "#9CA3AF", glow: "rgba(107, 114, 128, 0.2)" },
  muted: { primary: "#EF4444", secondary: "#FCA5A5", glow: "rgba(239, 68, 68, 0.2)" },
};

const STATE_LABELS: Record<FelixVoiceState, string> = {
  listening: "Listening",
  thinking: "Analyzing",
  speaking: "Speaking",
  paused: "Paused",
  muted: "Muted",
};

export function FelixAvatar({
  state,
  size = "md",
  variant = "orb",
  rolePreset,
  aggressiveness,
  showLabel = true,
  className,
}: FelixAvatarProps) {
  const dims = SIZE_MAP[size];
  const colors = STATE_COLORS[state];
  const center = dims.outer / 2;
  const uid = useId();
  const glowId = `felix-glow-${uid}-${state}`;
  const thinkGradientId = `felix-think-${uid}`;

  const animationClass = useMemo(() => {
    switch (state) {
      case "listening":
        return "felix-listening";
      case "thinking":
        return "felix-thinking";
      case "speaking":
        return "felix-speaking";
      case "muted":
        return "felix-muted";
      default:
        return "felix-paused";
    }
  }, [state]);

  return (
    <div className={cn("flex flex-col items-center gap-1", className)}>
      <div className="relative">
        {/* Outer glow ring */}
        <svg
          width={dims.outer}
          height={dims.outer}
          viewBox={`0 0 ${dims.outer} ${dims.outer}`}
          className={animationClass}
        >
          <defs>
            {/* Gradient for the outer ring */}
            <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={colors.glow} />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>

            {/* Animated dash pattern for thinking state */}
            <linearGradient id={thinkGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colors.primary} />
              <stop offset="50%" stopColor={colors.secondary} />
              <stop offset="100%" stopColor={colors.primary} />
            </linearGradient>
          </defs>

          {/* Background glow */}
          <circle
            cx={center}
            cy={center}
            r={dims.outer / 2 - 2}
            fill={`url(#${glowId})`}
            className={state === "speaking" ? "felix-glow-pulse" : ""}
          />

          {/* Outer ring */}
          <circle
            cx={center}
            cy={center}
            r={dims.ring / 2}
            fill="none"
            stroke={colors.primary}
            strokeWidth={state === "speaking" ? 2.5 : 1.5}
            strokeDasharray={state === "thinking" ? "4 4" : "none"}
            className={state === "thinking" ? "felix-ring-spin" : ""}
            opacity={state === "paused" ? 0.4 : 0.8}
          />

          {/* Inner core (Felix glyph) — geometry chosen by `variant`, color +
              opacity + breathe animation still driven by voice `state`. */}
          {renderCore({
            variant,
            center,
            inner: dims.inner,
            fill: colors.primary,
            opacity: state === "paused" ? 0.3 : state === "muted" ? 0.5 : 0.9,
            coreClass: state === "listening" ? "felix-core-breathe" : "",
          })}

          {/* "F" lettermark */}
          <text
            x={center}
            y={center}
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize={dims.fontSize}
            fontWeight="bold"
            fontFamily="system-ui, sans-serif"
          >
            F
          </text>

          {/* Speaking waveform bars */}
          {state === "speaking" && (
            <>
              {[-8, -4, 0, 4, 8].map((offset, i) => (
                <rect
                  key={i}
                  x={center + offset - 1}
                  y={center + dims.inner / 2 + 4}
                  width={2}
                  height={6}
                  rx={1}
                  fill={colors.secondary}
                  className="felix-waveform-bar"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </>
          )}

          {/* Muted X overlay */}
          {state === "muted" && (
            <>
              <line
                x1={center - dims.inner / 4}
                y1={center - dims.inner / 4}
                x2={center + dims.inner / 4}
                y2={center + dims.inner / 4}
                stroke="#EF4444"
                strokeWidth={2}
                strokeLinecap="round"
              />
              <line
                x1={center + dims.inner / 4}
                y1={center - dims.inner / 4}
                x2={center - dims.inner / 4}
                y2={center + dims.inner / 4}
                stroke="#EF4444"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </>
          )}

          {/* Thinking dots orbit — uses CSS custom property for size-aware radius */}
          {state === "thinking" && (
            <>
              {[0, 120, 240].map((angle, i) => {
                const orbitRadius = dims.ring / 2 - 4;
                return (
                  <circle
                    key={i}
                    cx={center}
                    cy={center}
                    r={2}
                    fill={colors.secondary}
                    className="felix-orbit-dot"
                    style={{
                      transformOrigin: `${center}px ${center}px`,
                      animationDelay: `${i * 0.3}s`,
                      // @ts-expect-error CSS custom property for orbit radius
                      "--orbit-radius": `${orbitRadius}px`,
                      animationName: "felix-orbit",
                      animationDuration: "2s",
                      animationTimingFunction: "linear",
                      animationIterationCount: "infinite",
                    }}
                  />
                );
              })}
            </>
          )}
        </svg>
      </div>

      {showLabel && (
        <div className="text-center">
          <span
            className="text-xs font-medium"
            style={{ color: colors.primary }}
          >
            {STATE_LABELS[state]}
          </span>
          {rolePreset && size !== "sm" && (
            <div className="text-[10px] text-muted-foreground capitalize">
              {rolePreset.replace(/_/g, " ")}
            </div>
          )}
        </div>
      )}

      {/* CSS animations — injected as a style tag to keep the component self-contained */}
      <style>{`
        .felix-listening {
          animation: felix-breathe 3s ease-in-out infinite;
        }
        .felix-thinking {
          animation: felix-pulse 1.5s ease-in-out infinite;
        }
        .felix-speaking {
          animation: none;
        }
        .felix-muted {
          opacity: 0.6;
        }
        .felix-paused {
          opacity: 0.5;
        }

        @keyframes felix-breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes felix-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.03); opacity: 0.9; }
        }

        .felix-core-breathe {
          animation: felix-core-pulse 3s ease-in-out infinite;
        }
        @keyframes felix-core-pulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }

        .felix-ring-spin {
          animation: felix-spin 4s linear infinite;
          transform-origin: center;
        }
        @keyframes felix-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .felix-glow-pulse {
          animation: felix-glow 0.8s ease-in-out infinite alternate;
        }
        @keyframes felix-glow {
          from { opacity: 0.6; }
          to { opacity: 1; }
        }

        .felix-waveform-bar {
          animation: felix-wave 0.5s ease-in-out infinite alternate;
        }
        @keyframes felix-wave {
          from { height: 3px; }
          to { height: 10px; }
        }

        @keyframes felix-orbit {
          from { transform: rotate(0deg) translateY(calc(-1 * var(--orbit-radius, 20px))); }
          to { transform: rotate(360deg) translateY(calc(-1 * var(--orbit-radius, 20px))); }
        }
      `}</style>
    </div>
  );
}
